import { useState, FormEvent } from 'react';

export default function ContactUs() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d8nrcuj97o3u3g1u648g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError('Something went wrong. Please try again or contact us directly by phone.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Page Header */}
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20corporate%20reception%20area%20with%20navy%20blue%20and%20orange%20branding%20accents%2C%20warm%20welcoming%20atmosphere%2C%20clean%20minimalist%20design%20with%20comfortable%20seating%2C%20world%20map%20wall%20decor%2C%20professional%20but%20friendly%20feel%2C%20natural%20lighting&width=1920&height=600&seq=contact-premium-visa&orientation=landscape"
          alt="Contact Premium Visa Solutions"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="Contact Premium Visa Solutions - Lusaka, Zambia"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 to-primary-950/65"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">Contact Us</h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-[550px] mx-auto">
            Get in touch with our team for professional visa support and enquiries
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
            {/* Contact Details */}
            <div className="lg:w-[420px] flex-shrink-0">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">Get In Touch</h2>

              <div className="space-y-5 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-foreground-900 mb-1">Office Address</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Plot No. 1194, Corner of Lunzua Road / Addis Ababa Road, Lusaka, Zambia
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-foreground-900 mb-1">Phone</h4>
                    <a href="tel:+260961311111" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer">
                      +260 961 311 111
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-foreground-900 mb-1">Email</h4>
                    <a href="mailto:info@pvs-zm.com" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer">
                      info@pvs-zm.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                    <i className="ri-global-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-foreground-900 mb-1">Website</h4>
                    <a href="https://www.pvs-zm.com" target="_blank" rel="nofollow" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer">
                      www.pvs-zm.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-lg overflow-hidden border border-secondary-200">
                <iframe
                  src="https://maps.google.com/maps?q=1194+Lunzua+Rd%2C+Lusaka%2C+Zambia&output=embed"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Premium Visa Solutions Office Location"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="flex-1">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-2">Send Us a Message</h2>
              <p className="text-sm text-foreground-600 mb-6">Fill out the form below and our team will get back to you within 24 hours.</p>

              {submitted ? (
                <div className="bg-accent-50 border border-accent-200 rounded-lg p-8 text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center bg-accent-500 text-background-50 rounded-full mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-2">Message Sent Successfully!</h3>
                  <p className="text-sm text-foreground-600 mb-4">Thank you for contacting Premium Visa Solutions. Our team will respond to your enquiry within 24 hours.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="px-8 py-2.5 bg-accent-500 text-background-50 font-medium rounded-md hover:bg-accent-600 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">
                      {error}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Full Name <span className="text-accent-600">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Phone Number <span className="text-accent-600">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="e.g. +260 961 311 111"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Email Address <span className="text-accent-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="Enter your email address"
                    />
                  </div>
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Country of Travel
                    </label>
                    <select
                      id="country"
                      name="country_of_travel"
                      className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer"
                    >
                      <option value="">Select destination country</option>
                      <option value="UAE">UAE</option>
                      <option value="Qatar">Qatar</option>
                      <option value="China">China</option>
                      <option value="South Africa">South Africa</option>
                      <option value="Namibia">Namibia</option>
                      <option value="DRC">DRC</option>
                      <option value="Egypt">Egypt</option>
                      <option value="Algeria">Algeria</option>
                      <option value="Zimbabwe">Zimbabwe</option>
                      <option value="Malawi">Malawi</option>
                      <option value="Lebanon">Lebanon</option>
                      <option value="Sri Lanka">Sri Lanka</option>
                      <option value="Indonesia">Indonesia</option>
                      <option value="Tanzania">Tanzania</option>
                      <option value="Thailand">Thailand</option>
                      <option value="Saudi Arabia">Saudi Arabia</option>
                      <option value="Turkey">Turkey</option>
                      <option value="India">India</option>
                      <option value="Cyprus">Cyprus</option>
                      <option value="Nigeria">Nigeria</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Message <span className="text-accent-600">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="Tell us how we can help you with your visa application..."
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">Maximum 500 characters</p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto px-10 py-3 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}